import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

//* HOC's
import { withDataContext, withUIContext } from 'context';

//* Components
import Container from 'components/common/Container';
import Text from 'components/common/Text';
import Image from 'components/common/Image';
import Paths from 'components/common/Paths';
import Button from 'components/common/Button';
import Tips from './Tips';
import SharePopup from 'components/common/Popups/SharePopup';
// import Editor from 'components/common/Editor'

//* Style
import AnchorStyle from './style';

const Anchor = ({ pageTitle, paths, image, infos, pageId, type, tips, addBookmark, checkIsBookmarked, removeBookmark, winHeight, openPopup, isPrivacy = false }) => {
    //! States
    const [activeIndex, setActiveIndex] = useState(null);
    const [isSaved, setIsSaved] = useState(false);

    //! Refs
    const descriptionItems = useRef([]);

    //! Check Is Saved
    useEffect(() => {
        setIsSaved(checkIsBookmarked({ type: type, id: pageId }));
    }, []);

    //! Open Share
    const openSharePopup = useCallback(() => {
        openPopup(<SharePopup />);
    }, [openPopup]);

    //! Handle Save Button Click
    const handleClickSaveButton = useCallback(() => {
        isSaved ? removeBookmark({ type: type, id: pageId }) : addBookmark({ type: type, id: pageId });

        setIsSaved(checkIsBookmarked({ type: type, id: pageId }));
    }, [type, pageId, isSaved]);

    //! Store content
    const storeContent = useMemo(() => {
        return infos.map((item, index) => {
            return (
                <div
                    key={index}
                    ref={(ref) => (descriptionItems.current[index] = ref)}>
                    <Text
                        className={'description-title'}
                        text={item.title}
                    />
                    <div dangerouslySetInnerHTML={{ __html: item.description }} />
                    {item.image && (
                        <Image
                            src={item.image.url}
                            alt={item.image.alt}
                        />
                    )}
                    {/* <Editor content={JSON.parse(item.description)} /> */}
                </div>
            );
        });
    }, [infos]);

    //! Scroll effect
    useEffect(() => {
        window.addEventListener('scroll', scroll);
        return () => {
            window.removeEventListener('scroll', scroll);
        };
    }, []);

    //! Scroll
    const scroll = () => {
        let arr = [];

        for (let i = 0; i < descriptionItems.current.length; i++) {
            if (descriptionItems.current[i].getBoundingClientRect().top < winHeight * 0.3) {
                arr.push(i);
            }
        }

        if (arr.length === 0) {
            setActiveIndex(0);
        } else {
            setActiveIndex(arr.length - 1);
        }
    };

    //! Handle list link click
    const handleListLinkClick = useCallback(
        (clickedIndex) => {
            window.scrollTo({
                top: window.pageYOffset + descriptionItems.current?.[clickedIndex].getBoundingClientRect().top - 100,
                behavior: 'smooth',
            });
        },
        [descriptionItems]
    );

    //! Store list
    const storeLinks = useMemo(() => {
        return infos.map(({ title }, index) => (
            <Text
                onClick={() => handleListLinkClick(index)}
                className={`p pS list-link ${index === activeIndex ? 'active font-ping-bolder' : 'font-ping-bold'}`}
                key={index}
                text={title}
            />
        ));
    }, [infos, activeIndex]);

    return (
        <AnchorStyle>
            {!isPrivacy && (
                <Paths
                    paths={paths}
                    pathColor={'primaryColor1'}
                />
            )}

            <Container section>
                <Text
                    tag={'h1'}
                    text={pageTitle}
                    className={'h3 anchor-title uppercase font-soundscape-bold'}
                />
                <Container
                    full
                    row
                    className='content-wrap'>
                    <div className='col-3 col-t-12 sticky-wrap'>
                        <div className='sticky'>
                            <div className='links-wrap'>{storeLinks}</div>
                            {!isPrivacy && (
                                <div className='btns-wrap'>
                                    <Button
                                        icon={'share'}
                                        text={'share'}
                                        className={'sticky-first-button'}
                                        color={'ColorApricot'}
                                        background={'ColorBigStone'}
                                        borderColor={'transparent'}
                                        hoverBorderColor={'transparent'}
                                        hoverBackground={'ColorApricot'}
                                        hoverColor={'ColorBigStone'}
                                        onClick={openSharePopup}
                                    />

                                    <Button
                                        icon={'bookmarks'}
                                        text={isSaved ? 'saved' : 'save'}
                                        color={isSaved ? 'ColorWhite' : 'ColorApricot'}
                                        background={isSaved ? 'ColorApricot' : 'transparent'}
                                        borderColor={'ColorApricot'}
                                        hoverColor={'ColorWhite'}
                                        hoverBorderColor={'ColorApricot'}
                                        hoverBackground={'ColorApricot'}
                                        onClick={handleClickSaveButton}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='col-9 col-t-12 main-content'>
                        {!isPrivacy && (
                            <Image
                                objectFit='cover'
                                className={'big-image'}
                                src={image.url}
                                alt={image.alt}
                                priority={true}
                            />
                        )}

                        <Container
                            full
                            row>
                            <div className='col-10 col-t-12 description'>{storeContent}</div>
                        </Container>

                        {!isPrivacy && tips?.title && tips?.children.length !== 0 && (
                            <Tips
                                title={tips.title}
                                slides={tips.children}
                            />
                        )}
                    </div>
                </Container>
            </Container>
        </AnchorStyle>
    );
};

export default withDataContext(withUIContext(Anchor, ['winHeight', 'openPopup']), ['addBookmark', 'checkIsBookmarked', 'removeBookmark']);
